import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebaseConfig';
import { collection, getDocs } from 'firebase/firestore'; // 'where' and 'query' have been removed
import { toast } from 'react-toastify';
import { CSVLink } from 'react-csv';

// Imports dos componentes MUI
import { Box, Paper, Typography, Grid, CircularProgress, Avatar, TextField, Button, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { green, blue, orange } from '@mui/material/colors';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BedIcon from '@mui/icons-material/Bed';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';

// Imports da biblioteca de Gráficos (Recharts)
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Componente do card de estatísticas
const StatCard = ({ title, value, icon, color }) => (
  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }} elevation={3}>
    <Avatar sx={{ bgcolor: color, width: 56, height: 56, mr: 2 }}>
      {icon}
    </Avatar>
    <Box>
      <Typography color="text.secondary">{title}</Typography>
      <Typography variant="h5" fontWeight="bold">{value}</Typography>
    </Box>
  </Paper>
);

// Função para pegar o primeiro e último dia do mês atual
const getPeriodoMesAtual = () => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    const formata = (data) => data.toISOString().split('T')[0];
    return { inicio: formata(primeiroDia), fim: formata(ultimoDia) };
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ faturamento: 0, taxaOcupacao: 0, checkins: 0 });
  const [barChartData, setBarChartData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [guestAnalysisData, setGuestAnalysisData] = useState([]);
  const [revenueByChaletData, setRevenueByChaletData] = useState([]);
  const [periodo, setPeriodo] = useState(getPeriodoMesAtual());
  const theme = useTheme();
  const PIE_CHART_COLORS = [theme.palette.primary.main, green[500], orange[500]];
  
  const [loadingMarketingReport, setLoadingMarketingReport] = useState(false);
  const [marketingData, setMarketingData] = useState([]);
  const csvLinkRef = useRef(null);

  useEffect(() => {
    if (marketingData.length > 0 && !loadingMarketingReport) {
      setTimeout(() => {
        if (csvLinkRef.current) {
            csvLinkRef.current.link.click();
        }
        setMarketingData([]);
      }, 500);
    }
  }, [marketingData, loadingMarketingReport]);

  useEffect(() => {
    const processarDados = (reservas, chales) => {
        const reservasNoPeriodo = reservas.filter(r => 
            r.dataCheckin >= periodo.inicio && r.dataCheckin <= periodo.fim
        );

        let faturamentoPeriodo = 0;
        let diariasOcupadasPeriodo = 0;
        reservasNoPeriodo.forEach(r => {
            faturamentoPeriodo += r.valorTotal || 0;
            diariasOcupadasPeriodo += r.numDiarias || 0;
        });
        const diasNoPeriodo = (new Date(periodo.fim) - new Date(periodo.inicio)) / (1000 * 60 * 60 * 24) + 1;
        const diariasDisponiveis = chales.length * diasNoPeriodo;
        const taxaOcupacao = diariasDisponiveis > 0 ? (diariasOcupadasPeriodo / diariasDisponiveis) * 100 : 0;
        setStats({
            faturamento: faturamentoPeriodo,
            taxaOcupacao: taxaOcupacao,
            checkins: reservasNoPeriodo.length
        });

        const pagamentosPorMetodo = { pix: 0, dinheiro: 0, cartao: 0 };
        reservasNoPeriodo.forEach(r => {
            if(r.adiantamento && r.adiantamento.metodo) {
                pagamentosPorMetodo[r.adiantamento.metodo] += r.adiantamento.valor || 0;
            }
            if(r.pagamentoRestante && r.pagamentoRestante.metodo) {
                pagamentosPorMetodo[r.pagamentoRestante.metodo] += r.pagamentoRestante.valor || 0;
            }
        });
        setPaymentMethodData([
            { name: 'PIX', value: pagamentosPorMetodo.pix },
            { name: 'Dinheiro', value: pagamentosPorMetodo.dinheiro },
            { name: 'Cartão', value: pagamentosPorMetodo.cartao },
        ].filter(item => item.value > 0));

        const faturamentoMensal = {};
        const hoje = new Date();
        for (let i = 5; i >= 0; i--) {
            const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
            const nomeMes = d.toLocaleString('pt-BR', { month: 'short' });
            const anoMesKey = `${d.getFullYear()}-${d.getMonth()}`;
            faturamentoMensal[anoMesKey] = { nome: nomeMes.replace('.', '').toUpperCase(), Faturamento: 0 };
        }
        reservas.forEach(r => {
            const dataCheckin = new Date(r.dataCheckin + 'T12:00:00');
            const anoMesKey = `${dataCheckin.getFullYear()}-${dataCheckin.getMonth()}`;
            if (faturamentoMensal[anoMesKey]) {
            faturamentoMensal[anoMesKey].Faturamento += r.valorTotal || 0;
            }
        });
        setBarChartData(Object.values(faturamentoMensal));
        
        const analiseHospedes = {
            'AABB_ARACAJU': { tipo: 'AABB Aracaju', Faturamento: 0, Reservas: 0 },
            'OUTRA_AABB':   { tipo: 'Outra AABB', Faturamento: 0, Reservas: 0 },
            'NAO_ASSOCIADO':{ tipo: 'Não Associado', Faturamento: 0, Reservas: 0 }
        };
        reservasNoPeriodo.forEach(r => {
            if (r.hospedeTipo && analiseHospedes[r.hospedeTipo]) {
            analiseHospedes[r.hospedeTipo].Faturamento += r.valorTotal || 0;
            analiseHospedes[r.hospedeTipo].Reservas += 1;
            }
        });
        setGuestAnalysisData(Object.values(analiseHospedes));

        const faturamentoPorChale = {};
        chales.forEach(chale => {
            if (chale.nome) {
            faturamentoPorChale[chale.nome] = { nome: chale.nome, Faturamento: 0 };
            }
        });
        reservasNoPeriodo.forEach(r => {
            if (r.chaleNome && faturamentoPorChale[r.chaleNome]) {
            faturamentoPorChale[r.chaleNome].Faturamento += r.valorTotal || 0;
            }
        });
        const sortedData = Object.values(faturamentoPorChale).sort((a, b) => a.Faturamento - b.Faturamento);
        setRevenueByChaletData(sortedData);
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        const [reservasSnapshot, chalesSnapshot] = await Promise.all([
          getDocs(collection(db, 'reservas')),
          getDocs(collection(db, 'chales'))
        ]);
        const reservasData = reservasSnapshot.docs.map(doc => {
            const data = doc.data();
            return { ...data, id: doc.id, hospedeTipo: data.hospedeTipo || 'NAO_ASSOCIADO' };
        });
        const chalesData = chalesSnapshot.docs.map(doc => doc.data());
        processarDados(reservasData, chalesData);
      } catch (error) {
        toast.error("Não foi possível carregar os dados do dashboard.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [periodo]);

  const handleExportarMarketing = async () => {
      setLoadingMarketingReport(true);
      try {
        const hospedesSnapshot = await getDocs(collection(db, 'hospedes'));
        const hospedesData = hospedesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            Nome: data.nome || 'Não informado',
            Contato: data.celular || 'Não informado',
            Email: data.email || 'Não informado'
          };
        });
  
        const uniqueHospedes = Array.from(new Map(hospedesData.map(item => [item['Contato'], item])).values());
        
        setMarketingData(uniqueHospedes);
        toast.success(`${uniqueHospedes.length} contatos preparados para download!`);
  
      } catch (error) {
        toast.error("Erro ao gerar o relatório de marketing.");
        console.error("Erro ao exportar contatos:", error);
      } finally {
        setLoadingMarketingReport(false);
      }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Skeleton do Cabeçalho */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight="600"><Skeleton width={200} /></Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="rectangular" width={150} height={40} />
              <Skeleton variant="rectangular" width={150} height={40} />
            </Box>
          </Box>
        </Paper>

        {/* Skeleton dos StatCards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}><Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} /></Grid>
          <Grid item xs={12} sm={6} md={4}><Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} /></Grid>
          <Grid item xs={12} sm={6} md={4}><Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} /></Grid>
        </Grid>
        
        {/* Skeleton dos Gráficos */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rectangular" height={500} sx={{ borderRadius: 1 }} /></Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
            <Grid item>
                <Typography variant="h5" fontWeight="600">Dashboard</Typography>
            </Grid>
            <Grid item xs>
                 <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
                    <Grid item>
                        <Button
                            variant="outlined"
                            startIcon={loadingMarketingReport ? <CircularProgress size={20} /> : <DownloadIcon />}
                            onClick={handleExportarMarketing}
                            disabled={loadingMarketingReport}
                        >
                            {loadingMarketingReport ? 'Gerando...' : 'Exportar Contatos'}
                        </Button>
                    </Grid>
                    <Grid item>
                        <TextField
                            label="Data de Início" type="date" size="small" value={periodo.inicio}
                            onChange={(e) => setPeriodo({...periodo, inicio: e.target.value})} InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item>
                         <TextField
                            label="Data de Fim" type="date" size="small" value={periodo.fim}
                            onChange={(e) => setPeriodo({...periodo, fim: e.target.value})} InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
      </Paper>
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title={`Faturamento no Período`}
            value={stats.faturamento.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            icon={<MonetizationOnIcon />}
            color={green[500]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Taxa de Ocupação no Período" 
            value={`${stats.taxaOcupacao.toFixed(1)}%`}
            icon={<BedIcon />}
            color={blue[500]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard 
            title="Check-ins no Período" 
            value={stats.checkins}
            icon={<EventIcon />}
            color={orange[500]}
          />
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, height: '400px' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>Faturamento Mensal (Últimos 6 Meses)</Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={barChartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nome" />
                    <YAxis tickFormatter={(value) => `R$${value.toLocaleString('pt-BR')}`} />
                    <Tooltip formatter={(value) => [value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), 'Faturamento']} />
                    <Legend />
                    <Bar dataKey="Faturamento" fill={theme.palette.primary.main} />
                  </BarChart>
                </ResponsiveContainer>
            </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '400px' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>Receita por Pagamento (no Período)</Typography>
                <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                        <Pie data={paymentMethodData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                             {paymentMethodData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => [value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), 'Total']} />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </Paper>
        </Grid>
        <Grid item xs={12}>
            <Paper sx={{ p: 2, height: '400px' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>Análise de Hóspedes por Tipo (no Período)</Typography>
                 <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={guestAnalysisData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="tipo" />
                        <YAxis />
                        <Tooltip 
                            formatter={(value, name) => {
                                if (name === 'Faturamento') {
                                    return [value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), 'Faturamento'];
                                }
                                return [value, 'Reservas'];
                            }}
                        />
                        <Legend />
                        <Bar dataKey="Faturamento" fill={green[500]} />
                        <Bar dataKey="Reservas" fill={orange[500]} />
                    </BarChart>
                </ResponsiveContainer>
            </Paper>
        </Grid>
        <Grid item xs={12}>
            <Paper sx={{ p: 2, height: '500px' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>Faturamento por Chalé (no Período)</Typography>
                 <ResponsiveContainer width="100%" height="90%">
                    <BarChart
                      layout="vertical"
                      data={revenueByChaletData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => `R$${value/1000}k`} />
                      <YAxis type="category" dataKey="nome" width={80} />
                      <Tooltip formatter={(value) => [value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), 'Faturamento']} />
                      <Legend />
                      <Bar dataKey="Faturamento" fill={theme.palette.secondary.main || blue[700]} />
                    </BarChart>
                </ResponsiveContainer>
            </Paper>
        </Grid>
      </Grid>

      {marketingData.length > 0 && (
          <CSVLink
              data={marketingData}
              headers={[{ label: "Nome", key: "Nome" }, { label: "Contato (Celular)", key: "Contato" }, { label: "Email", key: "Email" }]}
              filename={"relatorio_marketing_hospedes.csv"}
              ref={csvLinkRef}
              target="_blank"
              style={{ display: 'none' }} 
          />
      )}
    </Box>
  );
};

export default Dashboard;